<template>
  <form-block
    :title="tr('Lead generation forms')"
    :description="tr('Select a lead generation form to collect lead information from your ad.')"
  >
    <template #top-right>
      <refresh-button
        :label="tr('Update lead generation forms')"
        :success-message="tr('Lead generation forms updated successfully.')"
        :promise="() => refreshLeadgenForms()"
      />
    </template>

    <p v-if="!sourceProfile" class="m-0 text-muted-foreground">
      {{ tr('Please, select a source first.') }}
    </p>
    <template v-else>
      <select-field
        v-model="entity.settings.leadgen_form_id"
        :options="leadgenForms"
        :reduce="(obj) => obj.id"
        :disabled="disabled"
        :loading="isLoading"
        :placeholder="tr('Select a lead generation form')"
        label="name"
      />

      <info-text v-if="!leadgenTosAccepted" type="error">
        {{ tr('You can not use lead generation ads until your Facebook page has accepted the Facebook lead generation terms of service.') }} <br />
        <a :href="leadgenTosLink" target="_blank">
          {{ tr('Click here to accept the terms of service.') }}
        </a>

        <refresh-button
          inverted
          class="mt-2 justify-start"
          :label="tr('I accepted the terms of service, try again')"
          :success-message="tr('Profile refreshed successfully.')"
          :promise="() => refreshSourceProfile()"
        />
      </info-text>

      <info-text v-else type="info">
        {{ tr('You can create new lead generation forms here') }}:<br />
        <a :href="newFormLink" target="_blank"> {{ newFormLink }}</a>
      </info-text>
    </template>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import LeadgenFormService from '@/services/LeadgenForm.js';
import SourceProfileService from '@/services/SourceProfile.js';
import InfoText from '@/ui/components/InfoText.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';
import SelectField from '@/ui/components/SelectField.vue';

export default {
  name: 'LeadgenFormBlock',
  components: {
    InfoText,
    RefreshButton,
    SelectField,
  },
  mixins: [ RuleFormBlockMixin ],
  data: () => ({
    isLoading: false,
    leadgenForms: [],
  }),
  computed: {
    leadgenTosAccepted() {
      return this.sourceProfile?.leadgen_tos_accepted ?? false;
    },
    leadgenTosLink() {
      return `https://adsmanager.facebook.com/ads/leadgen/tos/?page_id=${this.sourceProfileExternalId}`;
    },
    newFormLink() {
      return `https://business.facebook.com/latest/instant_forms/forms/?asset_id=${this.sourceProfileExternalId}`;
    },
    sourceProfile() {
      if (
        !this.entity.source_profiles
        || this.entity.source_profiles.length === 0
        || !this.entity.source_profiles[0].id
      ) {
        return null;
      }

      return this.entity.source_profiles[0];
    },
    sourceProfileExternalId() {
      if (!this.sourceProfile) {
        return null;
      }
      if (this.sourceProfile.platform === 'instagram') {
        return this.sourceProfile.details.facebook_page_id;
      }
      return this.sourceProfile.external_id;
    },
    selectedLeadgenForm() {
      if (this.entity.settings.leadgen_form_id === null) {
        return null;
      }

      return this.leadgenForms.find(f => f.id === this.entity.settings.leadgen_form_id);
    },
  },
  watch: {
    isLoading(val) {
      if (!val) {
        this.clearUnavailableValues();
      }
    },
    sourceProfile: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.loadLeadgenForms();
        }
        else {
          this.leadgenForms = [];
          this.entity.settings.leadgen_form_id = null;
        }
      },
    },
  },
  mounted() {
    if (this.sourceProfile) {
      this.loadLeadgenForms();
    }
  },
  methods: {
    refreshSourceProfile() {
      return SourceProfileService.refreshDetails(this.sourceProfile.id)
        .then(sourceProfile => Object.assign(this.sourceProfile, sourceProfile));
    },
    loadLeadgenForms() {
      this.isLoading = true;

      const promise = LeadgenFormService.getList({ page: 1, limit: 1000, sourceProfileIds: [ this.sourceProfile.id ] });
      return this.updateLeadgenFormsList(promise)
        .finally(() => this.isLoading = false);
    },
    validate() {
      if (this.entity.settings.leadgen_form_id === null) {
        this.error(this.tr('Please select a lead generation form.'));
        return false;
      }
      return true;
    },
    refreshLeadgenForms() {
      this.isLoading = true;

      const promise = SourceProfileService.refreshLeadgenForms(this.sourceProfile.id);
      return this.updateLeadgenFormsList(promise)
        .finally(() => this.isLoading = false);
    },
    updateLeadgenFormsList(apiRequestPromise) {
      return apiRequestPromise
        .then(leadgenForms => this.leadgenForms = leadgenForms);
    },
    clearUnavailableValues() {
      if (!this.selectedLeadgenForm) {
        this.entity.settings.leadgen_form_id = null;
      }
    },
  },
};
</script>
